/**
 * Year6RevealPage — /year6/:slug/reveal
 *
 * Lightweight emotional peak. 5-second countdown → freeze leaderboard
 * → giant winning team announcement with confetti. Polls reveal-state
 * (server-driven so all classroom screens stay in sync).
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, GraduationCap } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_MS = 1000;

export default function Year6RevealPage() {
  const { slug } = useParams();
  const [eventId, setEventId] = useState(null);
  const [state, setState] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { const r = await axios.get(`${API}/events/slug/${slug}`); if (!cancelled) setEventId(r.data.event_id); }
      catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    const tick = async () => {
      try { const r = await axios.get(`${API}/events/${eventId}/year6/reveal-state`); if (!cancelled) setState(r.data); }
      catch { /* ignore */ }
    };
    tick();
    const t = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [eventId]);

  const phase = state?.phase || 'idle';
  const winner = state?.winner;
  const teams = state?.teams || [];

  return (
    <div className="relative min-h-screen overflow-hidden bg-neutral-950 text-white" data-testid="year6-reveal-page">
      <div className="absolute inset-0 opacity-50 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 30%, rgba(250,204,21,0.18), transparent 50%), radial-gradient(ellipse at 50% 80%, rgba(139,92,246,0.18), transparent 55%)' }} />
      <div className="relative z-10 flex items-center justify-between px-8 py-6 border-b border-white/5">
        <div className="inline-flex items-center gap-2">
          <GraduationCap size={22} weight="fill" className="text-amber-300" />
          <span className="text-[11px] font-black uppercase tracking-[0.22em] text-amber-200">Year 6 Final Results</span>
        </div>
        <p className="text-[11px] text-white/40 text-right">{state?.event_name || 'Class'}</p>
      </div>

      <div className="relative z-10 flex flex-col items-center justify-center min-h-[80vh] px-6 text-center">
        {phase === 'idle' && (
          <div data-testid="year6-reveal-idle">
            <Trophy size={48} weight="fill" className="text-amber-300 mx-auto mb-4 opacity-60" />
            <p className="text-[11px] font-black uppercase tracking-[0.22em] text-white/40 mb-3">Waiting for teacher</p>
            <h1 className="font-serif text-5xl font-black mb-3">Final results soon...</h1>
            <p className="text-white/55">Tap "Start Reveal" on the teacher controls.</p>
          </div>
        )}

        {phase === 'countdown' && (
          <AnimatePresence mode="wait">
            <motion.div
              key={`c-${state?.seconds_remaining}`}
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.4 }}
              transition={{ duration: 0.45, ease: 'easeOut' }}
              data-testid="year6-reveal-countdown"
            >
              <p className="text-[11px] font-black uppercase tracking-[0.22em] text-amber-300 mb-3">Winning team in...</p>
              <div className="font-mono font-black tabular-nums leading-none text-amber-300" style={{ fontSize: 'clamp(160px, 30vw, 360px)' }}>
                {state?.seconds_remaining || 0}
              </div>
            </motion.div>
          </AnimatePresence>
        )}

        {phase === 'reveal' && winner && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.85 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            data-testid="year6-reveal-winner"
          >
            <p className="text-[11px] font-black uppercase tracking-[0.22em] text-amber-300 mb-4">🏆 Winning Team</p>
            <div className="inline-flex w-32 h-32 sm:w-40 sm:h-40 items-center justify-center rounded-full mb-6 shadow-2xl" style={{ background: winner.color, boxShadow: `0 0 80px ${winner.color}88` }}>
              <Trophy size={64} weight="fill" className="text-white" />
            </div>
            <h1 className="font-serif font-black leading-none mb-3" style={{ fontSize: 'clamp(72px, 12vw, 180px)', color: winner.color, textShadow: `0 0 24px ${winner.color}55` }}>
              {(winner.name || '').toUpperCase()}!
            </h1>
            <p className="text-white/55 text-lg" data-testid="year6-reveal-score">{winner.score} points · {winner.participants} pupils</p>

            {/* Frozen leaderboard */}
            <div className="mt-10 max-w-md mx-auto rounded-2xl border border-white/10 bg-white/[0.04] p-5 text-left">
              <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/45 mb-3 text-center">Final Standings</p>
              <ul className="space-y-2">
                {teams.map((t, i) => (
                  <li key={t.id} className="flex items-center gap-3" data-testid={`year6-reveal-row-${t.id}`}>
                    <span className="w-5 text-white/45 text-[12px] font-black text-right">{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : ''}</span>
                    <span className="w-3 h-3 rounded-full" style={{ background: t.color }} />
                    <span className="flex-1 text-sm font-bold">{t.name}</span>
                    <span className="text-sm font-mono tabular-nums text-white/80">{t.score}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        )}
      </div>

      {/* Confetti — fires once when phase enters reveal */}
      {phase === 'reveal' && (
        <div className="fixed inset-0 pointer-events-none z-30" data-testid="year6-reveal-confetti">
          {Array.from({ length: 80 }).map((_, i) => {
            const left = Math.random() * 100;
            const delay = Math.random() * 1.5;
            const duration = 2.5 + Math.random() * 1.5;
            const color = ['#3B82F6', '#EF4444', '#22C55E', '#FACC15', '#8B5CF6', '#F472B6'][i % 6];
            return (
              <span key={i} className="absolute top-0 w-2.5 h-3.5 rounded-sm"
                style={{ left: `${left}%`, background: color, animation: `y6revealfall ${duration}s ${delay}s ease-in forwards` }} />
            );
          })}
        </div>
      )}
      <style>{`@keyframes y6revealfall { 0%{transform:translateY(-10vh) rotate(0deg);opacity:1} 100%{transform:translateY(110vh) rotate(720deg);opacity:0.3} }`}</style>
    </div>
  );
}
